import { z } from "zod";

/**
 * `GET` and `PUT /api/v1/bank/policy`, **as the OpenAPI document declares them** (`KIT-TOAST-D2` 3a, D-1020-7, `P1-LND-070`): the bank's own
 * credit policy, and beside it **the highest row of its grid, served**. `capacityRowGrade` is the grade of the row Capacité's instalment is
 * taken at (the highest rate; on a tie the EARLIER row of the stored grid keeps it, `highestGridRow` in `@hakili/domain-types`), and
 * `capacityRatePct` is its rate, dot-decimal (« 11.4 »); both `null` with no policy or no grid. The Politique's toast and callout read these:
 * the screen compares no rate itself. Strict on purpose: an extra key is a mismatch, not a surplus.
 */
const ONE_DECIMAL_RATE = /^\d{1,2}\.\d$/u;

/**
 * One definition, two readings (as `bank-decision-answer.ts`): the strict constructor holds the stubs and the OpenAPI parity (an extra key is a mismatch); the LENIENT one gives the twin the bank client parses
 * the live answer with, so a key the server adds later is not an error page (extra keys pass at every level). Every required field and type is identical; a missing required field still fails loudly.
 */
function buildPolicySchemas(object: typeof z.strictObject) {
  const gridRow = object({
    grade: z.string(),
    /** « 10.4 »: one decimal, as the row stores it. */
    ratePct: z.string().regex(ONE_DECIMAL_RATE),
  });
  const policy = object({
    rateTolerancePct: z.string(),
    durationMaxMonths: z.number().int(),
    vehicleAgeMaxYears: z.number().int(),
    vehicleAgeAtMaturityMaxYears: z.number().int(),
    taegCeilingPct: z.string(),
    delegationThresholdAmount: z.number().int(),
    assignableSharePct: z.number().int(),
    /**
     * `CHEF-SELF-VALIDATION` R1 (D-1029-2): « Le chef du crédit peut valider les dossiers qu'il instruit ». OFF until the bank sets it; the
     * Politique's « Délégation de pouvoir » toggle (D2 frames 181 to 184) binds it, and a `PUT` that omits it leaves it as it stands.
     */
    creditHeadValidatesOwnFiles: z.boolean(),
    gradeRates: z.array(gridRow),
  });
  /**
   * `PIECES-COUNT-SERVED` (`P1-DOS-068`): **the pièces the platform requires, served beside the policy** (bank-wide: no per-bank list). `shortLabel` is the form a sentence prints (« bulletins de salaire »,
   * already plural), `expectedFiles` how many documents a pièce asks for (`null` for one), so the screen draws served words and never a code-to-words table. READ-ONLY: the write schema does not take it.
   */
  const requiredDocument = object({
    code: z.string().min(1),
    shortLabel: z.string().min(1),
    expectedFiles: z.number().int().min(2).nullable(),
  });
  return { gridRow, policy, requiredDocument };
}
const strictPolicy = buildPolicySchemas(z.strictObject);
const loosePolicy = buildPolicySchemas(z.looseObject);

export const bankPolicyGridRowSchema = strictPolicy.gridRow;
export const bankPolicySchema = strictPolicy.policy;
export const requiredDocumentSchema = strictPolicy.requiredDocument;

const capacityFields = {
  /** The grade of the grid's highest row (« C », « B »…), `null` with no grid. */
  capacityRowGrade: z.string().nullable(),
  /** That row's rate, dot-decimal, `null` with no grid. */
  capacityRatePct: z.string().regex(ONE_DECIMAL_RATE).nullable(),
};

/** A bank's overrides of Hakili's underwriting figures, key to value. */
const overridesSchema = z.record(z.string(), z.union([z.number(), z.boolean()]));

/** The wire name of the served list (a WIRE value, ruled by the architect: data, not a name of ours). */
export const REQUIRED_DOCUMENTS_KEY = "requiredPieces" as const;

const readShape = (parts: typeof strictPolicy, object: typeof z.strictObject) =>
  object({
    policy: parts.policy.nullable(),
    ...capacityFields,
    ltvMaxPct: z.number().nullable(),
    underwriting_overrides: overridesSchema,
    [REQUIRED_DOCUMENTS_KEY]: z.array(parts.requiredDocument),
  });

export const bankPolicyReadSchema = readShape(strictPolicy, z.strictObject);
/** The bank client's reading of the same answer: unknown keys tolerated at every level. */
export const bankPolicyLenientReadSchema = readShape(loosePolicy, z.looseObject);

export const bankPolicyWriteSchema = z.strictObject({
  ...bankPolicySchema.shape,
  ...capacityFields,
  underwriting_overrides: overridesSchema,
});

export type BankPolicyRead = z.infer<typeof bankPolicyReadSchema>;
export type BankPolicyWrite = z.infer<typeof bankPolicyWriteSchema>;
